/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, ReactNode, useState, useEffect } from 'react';
import { EnvironmentContext } from '../../contexts';
import {
  MEMBERSHIP_MODELS,
  PLAYER_PORTALS_MODELS,
} from '@r-and-a-shared-ui/common-kontent-client';
import { kontentAiDataMapping } from '@r-and-a-shared-ui/core-data-mapping';
import { api } from '@r-and-a-shared-ui/randa-trpc-client';
import { hasValue } from '@r-and-a-shared-ui/utils';
import styles from './Link.module.scss';

export type LinkProps = {
  title: string;
  href: string;
  internal: boolean;
  openInNewTab?: boolean;
  icon?: string;
  children?: ReactNode;
  className?: string;
  codename?: string;
  hideIcon?: boolean;
  role?: string;
  kontentItemId?: string;
};

const LinkContent = ({
  icon,
  title,
  children,
  hideIcon = false,
}: {
  icon?: string;
  title: string;
  children?: ReactNode;
  hideIcon?: boolean;
  kontentItemId?: string;
}) => {
  const { Image } = useContext(EnvironmentContext);
  return (
    <>
      {!hideIcon && icon && (
        <span className={styles.icon}>
          <Image src={icon} alt={`${title} icon`} fill />
        </span>
      )}
      {children || title}
    </>
  );
};

const Anchor = ({
  href,
  title,
  openInNewTab,
  children,
  className,
  kontentItemId,
}: {
  href: string;
  title: string;
  openInNewTab?: boolean;
  children: ReactNode;
  className?: string;
  kontentItemId?: string;
}) => (
  <a
    className={className}
    href={href}
    title={title}
    target={openInNewTab ? '_blank' : undefined}
    rel={openInNewTab ? 'noopener noreferrer' : undefined}
    data-kontent-item-id={kontentItemId}
  >
    {children}
  </a>
);

export const Link = (props: LinkProps) => {
  const {
    title: propTitle,
    href: propHref,
    children,
    icon: propIcon,
    className,
    codename,
    hideIcon = false,
    role,
    kontentItemId,
  } = props;

  const hasPropsInfo = propHref && propTitle;
  const { Link: ProvidedLinkComponent } = useContext(EnvironmentContext);
  const [loadedLink, setLoadedLink] = useState<any>();
  const [isDataFetched, setIsDataFetched] = useState<boolean>(true);

  const [link, setLink] = useState<LinkProps>(props);

  const { title, href, internal, openInNewTab, icon } = link as LinkProps;

  const { data } = api.kontent.itemWithLinkedItems.useQuery({
    codename: isDataFetched ? '' : codename ?? '',
  });

  if (hasValue(data)) {
    const mappedLink = kontentAiDataMapping.link(
      data as MEMBERSHIP_MODELS.Link & PLAYER_PORTALS_MODELS.Link,
    );
    setLoadedLink(mappedLink);
    setIsDataFetched(true);
  }

  useEffect(() => {
    if (href || hasPropsInfo || !codename) {
      return;
    }
    setIsDataFetched(false);
  }, [codename, href, hasPropsInfo]);

  useEffect(() => {
    if (hasPropsInfo) {
      setLink({ ...props });
      return;
    }
    if (loadedLink) {
      setLink({ ...loadedLink });
      return;
    }
  }, [loadedLink, codename, hasPropsInfo, props]);

  if (ProvidedLinkComponent) {
    return (
      <ProvidedLinkComponent
        href={href}
        isExternal={!internal}
        openInNewTab={openInNewTab}
        className={className}
        role={role}
        kontentItemId={kontentItemId}
      >
        <LinkContent icon={icon} title={title} hideIcon={hideIcon}>
          {children}
        </LinkContent>
      </ProvidedLinkComponent>
    );
  }

  return (
    <Anchor
      href={href}
      title={title}
      openInNewTab={openInNewTab}
      kontentItemId={kontentItemId}
    >
      <LinkContent icon={icon} title={title}>
        {children}
      </LinkContent>
    </Anchor>
  );
};
